import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    AccessTime,
    CleaningServices,
    ReportProblem,
    Settings,
    CheckCircle,
    Bathroom,
    Wc,
    Group,
    TipsAndUpdates,
    ArrowRightAlt
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', title: 'はじめに' },
    { id: 'usage-time', title: 'お風呂・トイレの使用時間調整のコツ' },
    { id: 'cleaning-division', title: '掃除分担と衛生管理のポイント' },
    { id: 'trouble-cases', title: 'よくあるトラブル事例と解決法' },
    { id: 'rule-making', title: 'ルール作りのコツと運用のヒント' },
    { id: 'summary', title: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', lineHeight: 1.4 }}
                                    >
                                        {item.title}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの「お風呂・トイレ」問題、どう乗り越える？
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは経済的で楽しい一方、共有スペースの使い方で悩むことも。特に「お風呂・トイレ」は日々の生活に直結するため、意外とトラブルも多い場所です。
                    <br />
                    この記事では、使用時間の調整や掃除分担、よくあるトラブル事例とその解決法、そしてルール作りのコツまで、実践的なノウハウを分かりやすく解説します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                            <img
                                src="/images/blog/share-bathroom_roomshare-bathroom-toilet-rule-trouble.jpg"
                                alt="明るい照明と清潔感のある共有バスルームに複数の歯ブラシやタオルが整然と並び、ルームシェアの住人たちが快適に使えるよう工夫されている様子"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            「清潔で快適なバスルーム」は、ルームシェア生活の満足度を大きく左右します。
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* 使用時間調整のコツ */}
            <Box id="usage-time" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
                    お風呂・トイレの使用時間調整のコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    朝の支度や帰宅後のリラックスタイム——複数人が住むルームシェアでは、どうしても「お風呂やトイレのタイミング」が重なりがち。譲り合いと工夫で、スムーズな利用を目指しましょう。
                </Typography>
                <Card sx={{ mb: 2, maxWidth: 700 }}>
                    <CardContent>
                        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                            時間割ルールの例
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircle color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="朝は「1人15分まで」と上限を決める"
                                    secondary="目覚ましをかけ合い、ダラダラ長風呂を防ぐ"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircle color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="混雑しやすい時間帯は「順番表」を作成"
                                    secondary="出勤・通学時間を書き出してお互いに把握"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircle color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="急ぎの時は「一声かける」"
                                    secondary="コミュニケーションで小さな不満を防止"
                                />
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <TipsAndUpdates sx={{ fontSize: 20, mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                    <b>専門的アドバイス：</b><br />
                    共有カレンダー（Googleカレンダーやホワイトボード）を使うと、予定変更もスムーズ。特に朝の混雑が続く場合は「週替わりで優先枠」を設けるのもおすすめです。
                </Typography>
                <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>

                </Box>
            </Box>

            {/* 掃除分担と衛生管理 */}
            <Box id="cleaning-division" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <CleaningServices sx={{ mr: 1, verticalAlign: 'middle' }} />
                    掃除分担と衛生管理のポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    お風呂やトイレは、使い方次第ですぐに汚れやすい場所です。小さなことでも積み重なると、不満やトラブルの種に。だからこそ、掃除ルールは最初にしっかり決めておきましょう。
                </Typography>
                <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                        掃除分担のコツ
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <CleaningServices color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「曜日ごと」や「場所ごと」に担当を決める"
                                secondary="例：月曜はAさんがバスルーム、火曜はBさんがトイレ担当"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CleaningServices color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「気づいた人がサッと掃除」もOK"
                                secondary="ただし、負担が偏らないよう定期的に見直し"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CleaningServices color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「使った後は軽く拭く」を徹底"
                                secondary="水滴や髪の毛を残さない習慣が大切"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Chip label="衛生" color="primary" variant="outlined" />
                    <Chip label="分担" color="secondary" variant="outlined" />
                    <Chip label="ストレスフリー" color="success" variant="outlined" />
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <TipsAndUpdates sx={{ fontSize: 20, mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                    <strong>衛生維持のワンポイント：</strong> <br />
                    週1回の「みんなで大掃除デー」を設けると、短時間で一気にきれいにできて、チームワークもアップします。
                </Typography>
                <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>

                </Box>
            </Box>

            {/* トラブル事例と解決法 */}
            <Box id="trouble-cases" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <ReportProblem sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                    よくあるトラブル事例と解決法
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    どんなに仲が良くても、生活習慣や価値観の違いは避けられません。ここでは、実際に多いトラブルとその対処法を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" component="h3" color="error" sx={{ mb: 1 }}>
                                    ケース1：長風呂問題
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「毎朝or毎晩、長風呂で待たされる…」
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="タイマー導入で時間管理" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="事前に利用希望を共有（声かけルール）" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="「シャワーのみOK」の日を設定" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" component="h3" color="error" sx={{ mb: 1 }}>
                                    ケース2：トイレ掃除の押し付け合い
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「誰も掃除をしないまま、トイレが汚れてイライラ…」
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="掃除当番表の作成と見える化" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="掃除用具や洗剤を揃えて「やりやすく」工夫" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ArrowRightAlt color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary="月1回は2人で「リセット掃除」" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">
                    <b>ポイント：</b>トラブルは「放置」せず、早めに小さく解決するのが鉄則。言いにくいことも、グループチャットやメモを活用して「仕組み化」すると、個人攻撃にならず円満に済みます。
                </Typography>
            </Box>

            {/* ルール作りのコツ */}
            <Box id="rule-making" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
                    ルール作りのコツと運用のヒント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの「お風呂・トイレルール」は、細かすぎても厳しすぎても逆効果。生活の変化や新しい住人にも柔軟に対応できる運用が理想です。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                                <Group sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                良いルール作りのヒント
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="全員で話し合う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="「できる範囲」でOKの雰囲気作り" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="ルールを書き出して掲示" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="月1回の見直し・フィードバックタイム" />
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ borderRadius: 2, overflow: 'hidden', mt: { xs: 2, sm: 0 } }}>
                                <img
                                    src="/images/blog/rule-meeting_roomshare-bathroom-toilet-rule-trouble.jpg"
                                    alt="ナチュラルな木目調のテーブルを囲み、ルームメイトたちが笑顔で意見を出し合いながらルールを紙に書き出しているシーン。温かみのある照明で前向きな雰囲気"
                                    style={{ width: '100%', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <TipsAndUpdates sx={{ fontSize: 20, mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                    <b>補足：</b>ルームメイトが変わったら増えたら「最初にルールを説明」し、質問や提案も歓迎することで、トラブルを未然に防げます。
                </Typography>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#f3f6f9' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        お風呂・トイレの使い方や掃除分担は、「ちょっとした工夫」と「話し合い」で意外と快適に。トラブルもルールも、2人で作って2人で守るのがコツです。
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <Bathroom color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="使用時間は譲り合いと可視化がカギ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Wc color="secondary" />
                            </ListItemIcon>
                            <ListItemText primary="掃除分担と衛生ルールは明確に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ReportProblem color="warning" />
                            </ListItemIcon>
                            <ListItemText primary="トラブルは早めに、仕組みで解決" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Settings color="success" />
                            </ListItemIcon>
                            <ListItemText primary="定期的な見直しでルールをアップデート" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    ルームシェア生活をもっと快適に、もっと楽しく——今日から実践できるヒントをぜひ取り入れてみてください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-bathroom-toilet-rule-trouble',
    slug: 'roomshare-bathroom-toilet-rule-trouble',
    title: 'ルームシェアの「お風呂・トイレ」使用ルールとトラブル回避術',
    excerpt: 'お風呂・トイレの使用時間の調整方法、掃除分担のコツ、よくあるルームシェアのトラブル事例とその解決法、効果的なルール作りのポイントを徹底解説します。',
    coverImage: '/images/blog/cover-roomshare-bathroom_roomshare-bathroom-toilet-rule-trouble.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。カラフルなインテリアと開放感があり、協力的な雰囲気が伝わる',
    publishedAt: '2024-06-23T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '暮らしの知恵', 'トラブル対策'],
    tags: ['お風呂', 'トイレ', '掃除分担', 'ルール作り', 'トラブル解決'],
    author: {
        name: 'ルームシェア情報編集部',
        avatar: '/images/avatars/editor-team.jpg',
        bio: 'シェアハウス・ルームシェアの専門情報を発信する編集チーム。実体験や多くの取材事例に基づく、実用的な暮らしの知恵をお届けします。',
        profilePicture: '/images/avatars/editor-team.jpg'
    },
    viewCount: 1245,
    contentComponent: PostContent
};

export default post;